import { useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { AlertBanner } from '../components/AlertBanner.jsx';
import { mockSave } from '../services/botConfigService.js';

const defaults = { responseStyle: 'conversational', responseLength: 'balanced', responseFormat: 'paragraphs', responseEmoji: false };

export function ResponseStylePanel({ bot, updateBot, mockErrorOn, notify }) {
  const [style, setStyle] = useState(bot.responseStyle || defaults.responseStyle);
  const [length, setLength] = useState(bot.responseLength || defaults.responseLength);
  const [format, setFormat] = useState(bot.responseFormat || defaults.responseFormat);
  const [emoji, setEmoji] = useState(!!bot.responseEmoji);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const dirty = style !== (bot.responseStyle || defaults.responseStyle) ||
    length !== (bot.responseLength || defaults.responseLength) ||
    format !== (bot.responseFormat || defaults.responseFormat) || emoji !== !!bot.responseEmoji;

  function save() {
    if (!style || !length || !format) { setError('Please select a response style, length, and format.'); return; }
    setError(''); setStatus('saving');
    mockSave(mockErrorOn).then(() => {
      updateBot({ responseStyle: style, responseLength: length, responseFormat: format, responseEmoji: emoji });
      setStatus('success'); notify('success', 'Response style settings saved');
    }).catch(() => setStatus('error'));
  }

  function reset() {
    setStyle(bot.responseStyle || defaults.responseStyle);
    setLength(bot.responseLength || defaults.responseLength);
    setFormat(bot.responseFormat || defaults.responseFormat);
    setEmoji(!!bot.responseEmoji); setError(''); setStatus('idle');
  }

  return <section className="response-style-panel">
    <div className="panel-header"><h1>Response style configuration</h1><p>Choose how your chatbot writes answers, including tone style, answer length, format, and emoji use.</p></div>
    {status === 'error' && <AlertBanner text="Response style settings could not be saved. Please try again." onRetry={save} />}
    {status === 'success' && <div className="success-inline" role="status">Response style settings saved successfully.</div>}
    <div className="card">
      <div className="field">
        <label htmlFor="response-style">Response style</label>
        <select id="response-style" value={style} disabled={status==='saving'} onChange={e=>{setStyle(e.target.value);setStatus('idle');}}>
          <option value="conversational">Conversational — natural and friendly</option>
          <option value="professional">Professional — clear and business-focused</option>
          <option value="concise">Direct — brief and to the point</option>
          <option value="empathetic">Empathetic — warm and understanding</option>
        </select><div className="hint">Controls the writing style used in chatbot replies.</div>
      </div>
      <div className="field">
        <label htmlFor="response-length">Answer length</label>
        <select id="response-length" value={length} disabled={status==='saving'} onChange={e=>{setLength(e.target.value);setStatus('idle');}}>
          <option value="short">Short</option><option value="balanced">Balanced</option><option value="detailed">Detailed</option>
        </select><div className="hint">Sets the preferred amount of detail in each answer.</div>
      </div>
      <div className="field">
        <label htmlFor="response-format">Answer format</label>
        <select id="response-format" value={format} disabled={status==='saving'} onChange={e=>{setFormat(e.target.value);setStatus('idle');}}>
          <option value="paragraphs">Paragraphs</option><option value="bullets">Bullet points</option><option value="numbered">Numbered steps</option>
        </select><div className="hint">Choose how information should be organized.</div>
      </div>
      <div className="field"><div className="toggle-row"><div className="toggle-copy"><strong>Allow emojis</strong><span>Allow the bot to include emojis in responses.</span></div><button type="button" className={'switch'+(emoji?' on':'')} aria-label="Allow emojis" aria-pressed={emoji} disabled={status==='saving'} onClick={()=>{setEmoji(v=>!v);setStatus('idle');}}><span className="knob"/></button></div></div>
      <div className="response-style-preview" aria-live="polite"><strong>Preview</strong><p>{style==='professional'?'Thank you for your question. Here is a clear answer.':style==='concise'?'Here is the answer.':style==='empathetic'?'I understand your question. I’m happy to help.':'Sure! I’d be happy to help you with that.'}</p><small>Length: {length} · Format: {format} · Emojis: {emoji?'On':'Off'}</small></div>
      {error && <div className="error-text" role="alert"><Icon.alert s={12}/>{error}</div>}
      <div className="actions-row"><button type="button" className="btn-primary" onClick={save} disabled={status==='saving'||!dirty}>{status==='saving'&&<span className="spinner"/>}{status==='saving'?'Saving…':'Save changes'}</button><button type="button" className="btn-secondary" onClick={reset} disabled={status==='saving'||!dirty}>Reset</button></div>
      <p className="hint" style={{marginTop:12}}>Frontend prototype: saves through the mock service. API integration is pending approval of the response-style endpoint and payload.</p>
    </div>
  </section>;
}
